import { useEffect, useState } from 'react';
import { isRecapVisible, isRecapVisibleFor, RECAP_VISIBILITY_EVENT } from './recap-preference';

/**
 * The recap visibility preference as React state, kept in step with the stored value.
 *
 * Pass a conversation id for that thread's answer (the master switch AND its own opt-out); pass nothing for
 * the master switch alone, which is what the Settings row shows.
 *
 * Starts from the DEFAULT rather than from localStorage, and reads the real value in an effect: reading
 * storage during the first render makes the server and client disagree and React discards the markup. The
 * default is `true`, which is also the common case, so the correction is invisible unless the recap is
 * hidden — and then it is one frame.
 *
 * Listens to both the same-tab custom event and the cross-tab `storage` event, because the native one does
 * not fire in the tab that wrote the value.
 */
export function useRecapVisible(conversationId?: string): boolean {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const read = () => (conversationId ? isRecapVisibleFor(conversationId) : isRecapVisible());
    setVisible(read());
    const sync = () => setVisible(read());
    window.addEventListener(RECAP_VISIBILITY_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(RECAP_VISIBILITY_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [conversationId]);

  return visible;
}
